/**
 * MeasureDetailForm — auto-committing detail panel form for a single measure.
 *
 * This is NOT a dialog. It renders inline inside the master/detail right panel.
 * Fields dispatch diffs to the parent via `onChange` on every change/blur —
 * there is no per-item Save button. The global "Save all" in the TopBar pushes
 * to Qlik.
 *
 * MeasureNumberFormatField and MeasureColorField are co-located here (copied
 * from MasterItemsTab.tsx) so this file is self-contained.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { QlikMeasure } from "@repo/shared";
import { Chip, Field, Input, Textarea } from "@h2j-data-team/ui";
import {
  MeasureColorField,
  MeasureNumberFormatField,
} from "@/components/qlik/measure-format-fields";
import { QlikExpressionEditor } from "@/components/qlik/qlik-expression-editor";
import { cn } from "@/lib/utils";
import type { PendingMasterItemChange, PendingMasterItemCreate } from "@/stores/pending-changes";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type MeasureChanges = {
  title?: string;
  description?: string;
  expression?: string;
  label?: string;
  tags?: string[];
  numberFormat?: string;
  color?: string;
};

export interface MeasureDetailFormProps {
  /** Effective base object. For a brand-new item pass a blank template: { id: tempId, title: "", tags: [] } */
  item: QlikMeasure;
  /** true for pending-create or brand-new item */
  isCreate: boolean;
  /** Pending update change for this item (if any) */
  existingChange?: PendingMasterItemChange;
  /** Pending create record for this item (if any) */
  existingCreate?: PendingMasterItemCreate;
  /** Read-only when false */
  canEdit: boolean;
  /**
   * Called with a diff change-object (only changed fields).
   * Parent routes this to handleSaveMeasureToCache (edit) or
   * updateCreate (pending create) or addCreate (brand-new first edit).
   */
  onChange: (changes: MeasureChanges) => void;
}

// ---------------------------------------------------------------------------
// Tag pill row sub-component
// ---------------------------------------------------------------------------

function TagPillRow({
  id,
  tags,
  onChange,
  disabled,
  ariaDescribedBy,
}: {
  id: string;
  tags: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  ariaDescribedBy?: string;
}) {
  const { t } = useTranslation("single-app");
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const addTag = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed || tags.includes(trimmed)) return;
      onChange([...tags, trimmed]);
    },
    [tags, onChange],
  );

  const removeTag = useCallback(
    (tag: string) => {
      onChange(tags.filter((t) => t !== tag));
    },
    [tags, onChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(inputValue);
      setInputValue("");
    } else if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addTag(inputValue);
      setInputValue("");
    }
  };

  // The box looks like a field; clicking its empty part focuses the input (a mouse
  // convenience: the chips' remove buttons and the input are the keyboard controls).
  return (
    <div
      className={cn(
        "flex min-h-9 flex-wrap items-center gap-1.5 rounded-input border border-input bg-background px-3 py-1.5 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30",
        disabled && "cursor-not-allowed opacity-60",
      )}
      onClick={() => !disabled && inputRef.current?.focus()}
    >
      {tags.map((tag) =>
        disabled ? (
          <Chip key={tag}>{tag}</Chip>
        ) : (
          <Chip
            key={tag}
            onRemove={() => removeTag(tag)}
            removeLabel={t("editor.measure.removeTag", { tag })}
          >
            {tag}
          </Chip>
        ),
      )}
      {!disabled && (
        <input
          id={id}
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          aria-describedby={ariaDescribedBy}
          placeholder={tags.length === 0 ? `+ ${t("editor.measure.addTag")}` : ""}
          className="min-w-[80px] flex-1 bg-transparent text-xs outline-hidden placeholder:text-faint"
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Prefill helpers
// ---------------------------------------------------------------------------

function getEffectiveValues(
  item: QlikMeasure,
  isCreate: boolean,
  existingChange?: PendingMasterItemChange,
  existingCreate?: PendingMasterItemCreate,
): {
  title: string;
  description: string;
  expression: string;
  label: string;
  tags: string[];
  numberFormat: string;
  color: string;
} {
  if (isCreate && existingCreate) {
    return {
      title: existingCreate.title,
      description: existingCreate.description ?? "",
      expression: existingCreate.expression ?? "",
      label: existingCreate.label ?? "",
      tags: existingCreate.tags ?? [],
      numberFormat: existingCreate.numberFormat ?? "",
      color: existingCreate.color ?? "",
    };
  }

  if (existingChange) {
    return {
      title: existingChange.changes.title ?? item.title,
      description: existingChange.changes.description ?? item.description ?? "",
      expression: existingChange.changes.expression ?? item.expression ?? "",
      label: existingChange.changes.label ?? item.label ?? "",
      tags: existingChange.changes.tags ?? item.tags,
      numberFormat: existingChange.changes.numberFormat ?? item.numberFormat ?? "",
      color: existingChange.changes.color ?? item.color ?? "",
    };
  }

  return {
    title: item.title,
    description: item.description ?? "",
    expression: item.expression ?? "",
    label: item.label ?? "",
    tags: item.tags,
    numberFormat: item.numberFormat ?? "",
    color: item.color ?? "",
  };
}

// ---------------------------------------------------------------------------
// MeasureDetailForm
// ---------------------------------------------------------------------------

export function MeasureDetailForm({
  item,
  isCreate,
  existingChange,
  existingCreate,
  canEdit,
  onChange,
}: MeasureDetailFormProps) {
  const effective = getEffectiveValues(item, isCreate, existingChange, existingCreate);

  const [title, setTitle] = useState(effective.title);
  const [description, setDescription] = useState(effective.description);
  const [expression, setExpression] = useState(effective.expression);
  const [label, setLabel] = useState(effective.label);
  const [tags, setTags] = useState<string[]>(effective.tags);
  const [numberFormat, setNumberFormat] = useState(effective.numberFormat);
  const [color, setColor] = useState(effective.color);

  // Re-sync when item identity or pending state changes (e.g., switching
  // selection, or the pending change/create being cleared by "Discard all").
  useEffect(() => {
    const v = getEffectiveValues(item, isCreate, existingChange, existingCreate);
    setTitle(v.title);
    setDescription(v.description);
    setExpression(v.expression);
    setLabel(v.label);
    setTags(v.tags);
    setNumberFormat(v.numberFormat);
    setColor(v.color);
  }, [item, isCreate, existingChange, existingCreate]);

  // ---------------------------------------------------------------------------
  // Diff builders — only emit changed fields
  // ---------------------------------------------------------------------------

  const buildDiff = useCallback(
    (overrides: Partial<typeof effective>): MeasureChanges => {
      const current = {
        title,
        description,
        expression,
        label,
        tags,
        numberFormat,
        color,
        ...overrides,
      };

      const base = isCreate
        ? {
            title: existingCreate?.title ?? item.title,
            description: existingCreate?.description ?? item.description ?? "",
            expression: existingCreate?.expression ?? item.expression ?? "",
            label: existingCreate?.label ?? item.label ?? "",
            tags: existingCreate?.tags ?? item.tags,
            numberFormat: existingCreate?.numberFormat ?? item.numberFormat ?? "",
            color: existingCreate?.color ?? item.color ?? "",
          }
        : {
            title: item.title,
            description: item.description ?? "",
            expression: item.expression ?? "",
            label: item.label ?? "",
            tags: item.tags,
            numberFormat: item.numberFormat ?? "",
            color: item.color ?? "",
          };

      const diff: MeasureChanges = {};
      if (current.title !== base.title) diff.title = current.title;
      if (current.description !== base.description) diff.description = current.description;
      if (current.expression !== base.expression) diff.expression = current.expression;
      if (current.label !== base.label) diff.label = current.label;
      if (JSON.stringify(current.tags) !== JSON.stringify(base.tags)) diff.tags = current.tags;
      if (current.numberFormat !== base.numberFormat) diff.numberFormat = current.numberFormat;
      if (current.color !== base.color) diff.color = current.color;
      return diff;
    },
    [
      title,
      description,
      expression,
      label,
      tags,
      numberFormat,
      color,
      isCreate,
      existingCreate,
      item,
    ],
  );

  // ---------------------------------------------------------------------------
  // Per-field commit helpers — called on blur (text) or onChange (select/color)
  // ---------------------------------------------------------------------------

  const commitTitle = useCallback(
    (val: string) => {
      setTitle(val);
      const diff = buildDiff({ title: val });
      if (Object.keys(diff).length > 0) onChange(diff);
    },
    [buildDiff, onChange],
  );

  const commitDescription = useCallback(
    (val: string) => {
      setDescription(val);
      const diff = buildDiff({ description: val });
      if (Object.keys(diff).length > 0) onChange(diff);
    },
    [buildDiff, onChange],
  );

  const handleExpressionChange = useCallback(
    (val: string) => {
      setExpression(val);
      const diff = buildDiff({ expression: val });
      if (Object.keys(diff).length > 0) onChange(diff);
    },
    [buildDiff, onChange],
  );

  const commitLabel = useCallback(
    (val: string) => {
      setLabel(val);
      const diff = buildDiff({ label: val });
      if (Object.keys(diff).length > 0) onChange(diff);
    },
    [buildDiff, onChange],
  );

  const commitTags = useCallback(
    (val: string[]) => {
      setTags(val);
      const diff = buildDiff({ tags: val });
      if (Object.keys(diff).length > 0) onChange(diff);
    },
    [buildDiff, onChange],
  );

  const commitNumberFormat = useCallback(
    (val: string) => {
      setNumberFormat(val);
      const diff = buildDiff({ numberFormat: val });
      if (Object.keys(diff).length > 0) onChange(diff);
    },
    [buildDiff, onChange],
  );

  const commitColor = useCallback(
    (val: string) => {
      setColor(val);
      const diff = buildDiff({ color: val });
      if (Object.keys(diff).length > 0) onChange(diff);
    },
    [buildDiff, onChange],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const { t } = useTranslation("single-app");
  const idPrefix = `mdf-${item.id}`;

  return (
    <div className="grid gap-5">
      {/* Name (title) */}
      <Field label={t("masterItems.editMeasure.titleLabel")} id={`${idPrefix}-title`}>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={(e) => commitTitle(e.target.value)}
          placeholder={t("masterItems.editMeasure.titlePlaceholder")}
          disabled={!canEdit}
        />
      </Field>

      {/* Label */}
      <Field label={t("masterItems.editMeasure.labelLabel")} id={`${idPrefix}-label`}>
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={(e) => commitLabel(e.target.value)}
          placeholder={t("masterItems.editMeasure.labelPlaceholder")}
          disabled={!canEdit}
        />
      </Field>

      {/* Expression */}
      <Field
        label={t("masterItems.editMeasure.expressionLabel")}
        id={`${idPrefix}-expression`}
        hint={t("masterItems.editMeasure.expressionHint")}
      >
        {(control) => (
          <QlikExpressionEditor
            id={control.id}
            ariaLabel={t("masterItems.editMeasure.expressionLabel")}
            ariaDescribedBy={control["aria-describedby"]}
            value={expression}
            onChange={handleExpressionChange}
            placeholder={t("masterItems.editMeasure.expressionPlaceholder")}
            minHeight="140px"
            readOnly={!canEdit}
            showFx
          />
        )}
      </Field>

      {/* Number format + Color in a row */}
      <div className="grid grid-cols-2 items-start gap-4">
        <MeasureNumberFormatField
          key={item.id}
          value={numberFormat}
          onChange={commitNumberFormat}
          idPrefix={idPrefix}
          disabled={!canEdit}
          label={t("editor.measure.numberFormatLabel")}
          customOptionLabel={t("editor.measure.numberFormatCustom")}
        />
        <MeasureColorField
          value={color}
          onChange={commitColor}
          idPrefix={idPrefix}
          disabled={!canEdit}
          label={t("editor.measure.colorLabel")}
        />
      </div>

      {/* Tags */}
      <Field
        label={t("editor.measure.tagsLabel")}
        id={`${idPrefix}-tags`}
        hint={t("editor.measure.tagsHint")}
      >
        {(control) => (
          <TagPillRow
            id={control.id}
            ariaDescribedBy={control["aria-describedby"]}
            tags={tags}
            onChange={commitTags}
            disabled={!canEdit}
          />
        )}
      </Field>

      {/* Description */}
      <Field label={t("masterItems.editMeasure.descriptionLabel")} id={`${idPrefix}-description`}>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={(e) => commitDescription(e.target.value)}
          placeholder={t("masterItems.editMeasure.descriptionPlaceholder")}
          className="min-h-[72px]"
          disabled={!canEdit}
        />
      </Field>
    </div>
  );
}

export default MeasureDetailForm;
